import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { Loader2, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);
    const [googleError, setGoogleError] = useState('');
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    // Check for session expired message
    const sessionMessage = (location.state as { message?: string; type?: string })?.message;
    const isSessionExpired = (location.state as { type?: string })?.type === 'session_expired';

    const loginMutation = useMutation({
        mutationFn: () => api.login(formData.email, formData.password),
        onSuccess: (data) => {
            // Check if 2FA is required
            if (data.requires_2fa && data.temp_token) {
                navigate('/2fa-verify', {
                    state: {
                        tempToken: data.temp_token,
                        email: data.email,
                    },
                });
                return;
            }

            // Normal login (no 2FA)
            if (data.user && data.workspace !== undefined && data.access_token) {
                login(data.user, data.workspace, data.access_token);
                navigate('/dashboard');
            }
        },
    });

    const googleMutation = useMutation({
        mutationFn: (idToken: string) => api.loginWithGoogle(idToken),
        onSuccess: (data) => {
            login(data.user, data.workspace, data.access_token);
            navigate('/dashboard');
        },
        onError: (error) => {
            setGoogleError(error.message || 'Google sign-in failed');
        },
    });

    const handleGoogleSignIn = async () => {
        setGoogleError('');
        setIsGoogleLoading(true);
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const idToken = await result.user.getIdToken();
            googleMutation.mutate(idToken);
        } catch (error: any) {
            if (error.code === 'auth/popup-closed-by-user') {
                // User closed popup, don't show error
                setIsGoogleLoading(false);
                return;
            }
            setGoogleError(error.message || 'Google sign-in failed');
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        loginMutation.mutate();
    };

    const isLoading = isGoogleLoading || googleMutation.isPending;

    return (
        <div className="animate-fade-in">
            <h2 className="text-2xl font-bold mb-2">{t('auth.loginTitle')}</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                {t('auth.loginSubtitle')}
            </p>

            {/* Session Expired Message */}
            {isSessionExpired && sessionMessage && (
                <div className="mb-6 p-4 bg-warning-50 dark:bg-warning-600/20 border border-warning-200 dark:border-warning-600/30 rounded-lg">
                    <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-warning-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-warning-800 dark:text-warning-200 text-sm font-medium">
                                {sessionMessage}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Google Sign-In Button */}
            <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                )}
                <span className="font-medium">
                    {isLoading ? t('common.loading') : t('auth.loginWithGoogle')}
                </span>
            </button>

            {googleError && (
                <div className="p-3 mb-4 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                    {googleError}
                </div>
            )}

            <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-300 dark:border-neutral-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-neutral-800 text-neutral-500">
                        {t('auth.orLoginEmail')}
                    </span>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="email" className="label">{t('auth.email')}</label>
                    <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input"
                        placeholder="you@example.com"
                        required
                    />
                </div>

                <div>
                    <label htmlFor="password" className="label">{t('auth.password')}</label>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="input pr-10"
                            placeholder="••••••••"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <div className="text-right mt-1">
                        <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                            {t('auth.forgotPassword')}
                        </Link>
                    </div>
                </div>

                {loginMutation.isError && (
                    <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                        {loginMutation.error?.message || 'Login failed. Please try again.'}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loginMutation.isPending}
                    className="btn-primary w-full"
                >
                    {loginMutation.isPending ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('common.loading')}
                        </>
                    ) : (
                        t('auth.login')
                    )}
                </button>
            </form>

            {/* DEV ONLY: Demo Login */}
            {import.meta.env.DEV && (
                <div className="mt-6">
                    <div className="relative mb-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-dashed border-warning-300 dark:border-warning-600"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-white dark:bg-neutral-800 text-warning-500">
                                🧪 Dev Only
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const data = await api.demoLogin();
                                login(data.user, data.workspace, data.access_token);
                                navigate('/dashboard');
                            } catch (e: any) {
                                console.error('Demo login failed:', e);
                                alert('Demo login failed. Is backend running on :8787?');
                            }
                        }}
                        className="w-full py-2 px-4 bg-warning-100 dark:bg-warning-600/20 border border-warning-300 dark:border-warning-600 text-warning-700 dark:text-warning-300 rounded-lg hover:bg-warning-200 dark:hover:bg-warning-600/30 transition-colors text-sm font-medium"
                    >
                        🚀 Demo Login (dev only)
                    </button>
                </div>
            )}

            <p className="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-400">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                    {t('auth.register')}
                </Link>
            </p>
        </div>
    );
}
